import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  MenuItem,
  InputAdornment,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import CustomButton from 'lib/designSystem/Button';
import { SGR_TYPES, CURRENCIES, CONVERSION_RATES } from 'lib/consts';
import { validaRut } from 'lib/functions/utils';
import { useDispatch } from 'react-redux';
import DetailedRiskPremiumCalculator from './DetailedRiskPremiumCalculator';
import { getDollarValue, getUfValue } from 'lib/api/cmf';
import { updateSGRWarrantyRequest } from 'lib/api/sgr';
import { showAlert } from 'lib/functions/utils';
import { getCreditLineByProduct } from 'lib/api/creditLine';
import WarrantyEditConfirmation from './WarrantyEditConfirmation';
import { Warranty } from 'lib/types/models';
import type { RiskPremiumRates } from 'lib/functions/riskPremiumCalculator';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';

interface EditableSummaryProps {
  warranty: Warranty;
  setIsEditing: (isEditing: boolean) => void;
  refreshWarranty?: () => void;
}

interface WarrantyFormData {
  type: string;
  quotedAmount: string;
  quotedCurrency: string;
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  tenderId: string;
  principalRut: string;
}

const useCreditLineData = (businessIdentifier: string | null) => {
  const dispatch = useDispatch();
  const [subProductsData, setSubProductsData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCreditLineData = async () => {
      try {
        setIsLoading(true);
        if (!businessIdentifier) {
          setSubProductsData([]);
          return;
        }
        const creditLine = await getCreditLineByProduct(
          dispatch,
          businessIdentifier,
          'sgr',
        );
        const data = creditLine?.subProducts || creditLine || [];
        setSubProductsData(data);
      } catch (error) {
        setError(error instanceof Error ? error.message : 'An error occurred');
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchCreditLineData();
  }, [businessIdentifier, dispatch]);

  return {
    subProductsData,
    isLoading,
    error,
  };
};

const EditableSummary: React.FC<EditableSummaryProps> = ({
  warranty,
  setIsEditing,
  refreshWarranty,
}) => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState<WarrantyFormData>({
    type: warranty.type || '',
    quotedAmount: warranty.quotedAmount?.toString() || '',
    quotedCurrency: warranty.quotedCurrency || 'CLP',
    startDate: warranty.startDate ? dayjs(warranty.startDate) : null,
    endDate: warranty.endDate ? dayjs(warranty.endDate) : null,
    tenderId: warranty.tenderId || '',
    principalRut: warranty.principalRut || '',
  });
  const [displayAmount, setDisplayAmount] = useState('');
  const [daysDifference, setDaysDifference] = useState<number>(0);
  const [rutError, setRutError] = useState<string | null>(null);
  const [dateError, setDateError] = useState<string | null>(null);
  const [conversionRates, setConversionRates] = useState<any>(CONVERSION_RATES);
  const [rateControls, setRateControls] = useState<RiskPremiumRates>({
    variableRate: warranty.operationCostVariable,
    fixedRate: warranty.operationCostFixed,
    variableDiscount: warranty.discountRateVariable,
    fixedDiscount: warranty.discountRateFixed,
  });
  const [isConfirmationOpen, setConfirmationOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [areRatesLoaded, setAreRatesLoaded] = useState(false);

  const { subProductsData } = useCreditLineData(warranty.businessIdentifier);

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^\d]/g, '');
    setFormData((prev) => ({ ...prev, quotedAmount: value }));
  };

  const updateFormData = (updates: Partial<WarrantyFormData>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  };

  const handleOpenConfirmation = () => {
    if (isFormValid()) setConfirmationOpen(true);
  };

  const handleConfirmEdit = async (runDecisionEngine: boolean) => {
    try {
      setIsLoading(true);
      const body = {
        ...formData,
        quotedAmount: Number(formData.quotedAmount),
        conversionRate: conversionRates[formData.quotedCurrency],
        businessIdentifier: warranty.businessIdentifier,
        operationCostFixed: rateControls.fixedRate,
        operationCostVariable: rateControls.variableRate,
        discountRateFixed: rateControls.fixedDiscount,
        discountRateVariable: rateControls.variableDiscount,
        runDecisionEngine,
      };
      console.log('body', body);
      const response = await updateSGRWarrantyRequest(
        dispatch,
        warranty.id,
        body,
      );
      if (response) {
        setConfirmationOpen(false);
        setIsEditing(false);
        if (refreshWarranty) refreshWarranty();
        showAlert(dispatch, 'success', 'Garantía actualizada exitosamente');
      }
    } catch (error: any) {
      const message = error.response?.data?.message
        ? error.response.data.message
        : 'Error al actualizar la garantía';
      showAlert(dispatch, 'error', message);
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchConversionRates = async () => {
    try {
      const [dollarResponse, ufResponse] = await Promise.all([
        getDollarValue(dispatch),
        getUfValue(dispatch),
      ]);

      const newRates = {
        USD: dollarResponse?.dolar || CONVERSION_RATES.USD,
        UF: ufResponse?.uf || CONVERSION_RATES.UF,
        CLP: CONVERSION_RATES.CLP,
      };

      setConversionRates(newRates);

      const hasRealRates = dollarResponse?.dolar && ufResponse?.uf;
      setAreRatesLoaded(hasRealRates);
    } catch (error) {
      console.error('Error fetching conversion rates:', error);
      setConversionRates(CONVERSION_RATES);
      setAreRatesLoaded(false);
    }
  };

  // Load conversion rates
  useEffect(() => {
    fetchConversionRates();
  }, []);

  // Handle amount formatting
  useEffect(() => {
    if (formData.quotedAmount) {
      const num = parseInt(formData.quotedAmount, 10);
      if (!isNaN(num)) {
        const formatted = num.toLocaleString('de-DE');
        setDisplayAmount(formatted);
      } else {
        setDisplayAmount('');
      }
    } else {
      setDisplayAmount('');
    }
  }, [formData.quotedAmount]);

  // Calculate days difference
  useEffect(() => {
    if (formData.startDate && formData.endDate) {
      const start = formData.startDate.toDate();
      const end = formData.endDate.toDate();
      const days = Math.ceil(
        (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24),
      );
      setDaysDifference(days);
    } else {
      setDaysDifference(0);
    }
  }, [formData.startDate, formData.endDate]);

  // Validate dates
  useEffect(() => {
    if (formData.startDate && formData.endDate) {
      if (formData.endDate.isBefore(formData.startDate, 'day')) {
        setDateError(
          'La fecha de término debe ser posterior a la fecha de inicio',
        );
      } else {
        setDateError(null);
      }
    } else {
      setDateError(null);
    }
  }, [formData.startDate, formData.endDate]);

  // Validate RUT
  useEffect(() => {
    if (formData.principalRut && !validaRut(formData.principalRut)) {
      setRutError('RUT inválido');
    } else {
      setRutError(null);
    }
  }, [formData.principalRut]);

  const isFormValid = () => {
    return (
      formData.type &&
      formData.quotedAmount &&
      formData.quotedCurrency &&
      formData.startDate &&
      formData.endDate &&
      formData.tenderId &&
      formData.principalRut &&
      !rutError &&
      !dateError
    );
  };

  const isApproved = warranty.status === 'APPROVED';
  const needsConversionRate = formData.quotedCurrency !== 'CLP';

  const isRateReady =
    !needsConversionRate ||
    (needsConversionRate &&
      areRatesLoaded &&
      conversionRates[
        formData.quotedCurrency as keyof typeof CONVERSION_RATES
      ] !==
        CONVERSION_RATES[
          formData.quotedCurrency as keyof typeof CONVERSION_RATES
        ]);

  return (
    <>
      <Box sx={{ p: 3 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="ID Licitación"
                  value={formData.tenderId}
                  onChange={(e) => updateFormData({ tenderId: e.target.value })}
                  inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                  required
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="RUT Mandante"
                  value={formData.principalRut}
                  onChange={(e) =>
                    updateFormData({
                      principalRut: e.target.value.toUpperCase(),
                    })
                  }
                  placeholder="12345678-9"
                  error={!!rutError}
                  helperText={rutError || 'Ej: 12345678-9'}
                  required
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label="Tipo de Garantía"
                  value={formData.type}
                  onChange={(e) => updateFormData({ type: e.target.value })}
                  required
                >
                  {SGR_TYPES.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Monto Cotizado"
                  type="text"
                  value={displayAmount}
                  onChange={handleAmountChange}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {formData.quotedCurrency}
                      </InputAdornment>
                    ),
                  }}
                  required
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label="Moneda"
                  value={formData.quotedCurrency}
                  onChange={(e) =>
                    updateFormData({
                      quotedCurrency: e.target.value,
                    })
                  }
                  required
                >
                  {CURRENCIES.map((currency) => (
                    <MenuItem key={currency.value} value={currency.value}>
                      {currency.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Fecha Inicio"
                    value={formData.startDate}
                    onChange={(date) => updateFormData({ startDate: date })}
                    slotProps={{
                      textField: { fullWidth: true, required: true },
                    }}
                  />
                </LocalizationProvider>
              </Grid>

              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Fecha Término"
                    value={formData.endDate}
                    onChange={(date) => updateFormData({ endDate: date })}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!dateError,
                        helperText: dateError,
                        required: true,
                      },
                    }}
                  />
                </LocalizationProvider>
                {!dateError && daysDifference !== 0 && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 1, display: 'block' }}
                  >
                    Duración de la garantía: {daysDifference} días
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} md={6}>
            {isRateReady ? (
              <DetailedRiskPremiumCalculator
                amount={parseInt(formData.quotedAmount) || 0}
                days={daysDifference}
                conversionRate={
                  conversionRates[formData.quotedCurrency] ||
                  conversionRates.CLP
                }
                type={formData.type}
                businessIdentifier={warranty.businessIdentifier}
                rateControls={rateControls}
                setRateControls={setRateControls}
                useConcentrationRates={false}
                canEditRates={true}
              />
            ) : (
              <CenteredCircularProgress relative />
            )}
          </Grid>
        </Grid>

        <Box
          sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}
        >
          <CustomButton
            color="secondary"
            onClick={() => setIsEditing(false)}
            disabled={isLoading}
          >
            Cancelar
          </CustomButton>
          <CustomButton
            color="primary"
            onClick={handleOpenConfirmation}
            disabled={!isFormValid() || isLoading || !isRateReady}
          >
            {isLoading ? 'Guardando...' : 'Guardar Cambios'}
          </CustomButton>
        </Box>
      </Box>

      <WarrantyEditConfirmation
        open={isConfirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        onConfirm={handleConfirmEdit}
        formData={{
          ...formData,
          conversionRate:
            conversionRates[formData.quotedCurrency] || conversionRates.CLP,
        }}
        rateControls={rateControls}
        subProductsData={subProductsData}
        isApproved={isApproved}
        originalWarranty={warranty}
      />
    </>
  );
};

export default EditableSummary;
